import { useEffect, useRef } from "react";

import { Message } from "components/TournamentChat";
import CheveronLeftBold from "shared/icons/CheveronLeftBold";
import ProfileBold from "shared/icons/ProfileBold";
import MessageOutline from "shared/icons/MessageOutline";
import SingleTickUnBold from "shared/icons/SingleTickUnBold";

interface MessageChatProps {
  className?: string;
  id?: string;
  messages: Message[];
  maxHeight?: string | number;
  setLimit?: (limit: any) => void;
  isDropDown: {
    open: boolean;
    messageId: string;
  };
  hasMore?: boolean;
  openDropdownModal: (messageId: string) => void;
  closeDropdownModal: () => void;
  openDeleteModal: (messageId: string) => void;
  loadMoreMessages: () => void;
}

function groupMessagesByDate(messages: Message[]) {
  return messages?.reduce<Record<string, Message[]>>((acc, msg) => {
    if (!acc[msg.date]) acc[msg.date] = [];
    acc[msg.date].push(msg);
    return acc;
  }, {});
}

function groupBySender(messages: Message[]) {
  const grouped: {
    sender: "me" | "other";
    profileImage?: string;
    name?: string;
    messages: Message[];
  }[] = [];

  let currentGroup: (typeof grouped)[0] | null = null;

  for (const msg of messages) {
    if (
      !currentGroup ||
      currentGroup.sender !== msg.sender ||
      currentGroup.profileImage !== msg.profileImage
    ) {
      currentGroup = {
        sender: msg.sender,
        profileImage: msg.profileImage,
        name: msg.name,
        messages: [msg],
      };
      grouped.push(currentGroup);
    } else {
      currentGroup.messages.push(msg);
    }
  }

  return grouped;
}

function formatDateLabel(dateString: string) {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const isToday =
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate();

  const isYesterday =
    date.getFullYear() === yesterday.getFullYear() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getDate() === yesterday.getDate();

  if (isToday) return "Today";
  if (isYesterday) return "Yesterday";

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default function MessageChat({
  className,
  id = "space",
  messages,
  maxHeight = 300,
  setLimit,
  isDropDown,
  hasMore,
  openDropdownModal,
  closeDropdownModal,
  loadMoreMessages,
  openDeleteModal,
}: MessageChatProps) {
  const groupedMessages = groupMessagesByDate(messages);
  const messageContainerRef = useRef<HTMLDivElement>(null);
  const messageEndRef = useRef<HTMLDivElement>(null);
  const prevMessageCount = useRef(messages.length);
  const isInitialScrollDone = useRef(false);
  const preventScrollRef = useRef(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        if (isDropDown?.open) {
          closeDropdownModal();
        }
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropDown, closeDropdownModal]);

  useEffect(() => {
    const container = messageContainerRef.current;
    if (!container || messages?.length === 0) return;

    const newMessageCount = messages?.length;
    const addedMessages = newMessageCount > prevMessageCount.current;

    if (!isInitialScrollDone.current) {
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
      isInitialScrollDone.current = true;
    } else if (addedMessages && !preventScrollRef.current) {
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    }

    prevMessageCount.current = newMessageCount;
    preventScrollRef.current = false;
  }, [messages]);

  return (
    <div id={`${id}-chat`} className="container-tournament-chat" ref={messageContainerRef}>
      {hasMore && (
        <div
          className={"load-more"}
          onClick={() => {
            preventScrollRef.current = true;
            loadMoreMessages();
          }}
        >
          <h6 className={"load-more-msg"}>View Older Messages</h6>
        </div>
      )}
      {Object.entries(groupedMessages)
        .sort(
          ([dateA], [dateB]) =>
            new Date(dateA).getTime() - new Date(dateB).getTime()
        )
        .map(([date, msgs]) => {
          const sortedMsgs = [...msgs].sort(
            (a, b) =>
              new Date(a.createdAt || "").getTime() -
              new Date(b.createdAt || "").getTime()
          );

          const senderGroups = groupBySender(sortedMsgs);

          return (
            <div key={date} className="divided_container">
              <div className="day_container">
                <div className="day">
                  <div className="text_day">{formatDateLabel(date)}</div>
                </div>
              </div>

              {senderGroups.map((group, i) =>
                group.sender === "other" ? (
                  <div key={i} className="sender_container">
                    <div className="profile-container">
                      {group.profileImage ? (
                        <img
                          src={group.profileImage}
                          alt="Group profile"
                          className="sender_profile"
                          width={320}
                          height={320}
                          loading="lazy"
                        />
                      ) : (
                        <ProfileBold fontSize={24} />
                      )}
                    </div>
                    <div className="message_container">
                      <div className="name-container">
                        {group?.name && (
                          <div className="name">{group?.name}</div>
                        )}
                      </div>
                      {group.messages.map((msg) => (
                        <div
                          key={msg?.id}
                          className="message_box"
                          ref={dropdownRef}
                        >
                          <div className="d_flex">
                            {!msg?.isDeleted && (
                              <div
                                className="drop-down-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDropdownModal(msg.id);
                                }}
                              >
                                <CheveronLeftBold
                                  rotate={
                                    isDropDown?.messageId === msg.id &&
                                    isDropDown.open
                                      ? 90
                                      : 270
                                  }
                                />

                                {isDropDown?.messageId === msg.id &&
                                  isDropDown.open && (
                                    <div
                                    className={`drop-down-wrapper ${group.sender === "other" ? "left-align" : ""}`}
                                    ref={dropdownRef}
                                    >
                                      {/* <div className="drop-down-item">Edit</div> */}
                                      <div
                                        className="drop-down-item delete"
                                        onClick={(e) => {
                                          openDeleteModal(msg.id);
                                        }}
                                      >
                                        Delete
                                      </div>
                                    </div>
                                  )}
                              </div>
                            )}
                            {msg.isDeleted ? (
                              <p className={"deleted-message"}>
                                {" "}
                                <span> {/* <CrossOutlined /> */}</span> This
                                message was deleted
                              </p>
                            ) : (
                              <div className="text">{msg.text}</div>
                            )}{" "}
                          </div>
                          <div className="text">{msg.time}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div key={i} className="receiver_container">
                    <div className="message_container_me">
                      {group.messages.map((msg) => (
                        <div
                          key={msg?.id}
                          className="message_box_me"
                          ref={dropdownRef}
                        >
                          <div className="d_flex">
                            {msg.isDeleted ? (
                              <p className={"deleted-message"}>
                                {" "}
                                <span> {/* <CrossOutlined /> */}</span> This
                                message was deleted
                              </p>
                            ) : (
                              <div className="text">{msg.text}</div>
                            )}
                            <div
                              className="drop-down-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDropdownModal(msg.id);
                              }}
                            >
                              {!msg?.isDeleted && (
                                <CheveronLeftBold
                                  rotate={
                                    isDropDown?.messageId === msg.id &&
                                    isDropDown.open
                                      ? 90
                                      : 270
                                  }
                                />
                              )}

                              {isDropDown?.messageId === msg.id &&
                                isDropDown.open && (
                                  <div
                                    className="drop-down-wrapper"
                                    ref={dropdownRef}
                                  >
                                    {/* <div className="drop-down-item">Edit</div> */}
                                    <div
                                      className="drop-down-item delete"
                                      onClick={(e) => {
                                        openDeleteModal(msg.id);
                                      }}
                                    >
                                      Delete
                                    </div>
                                  </div>
                                )}
                            </div>
                          </div>
                          <div className="d_flex">
                            <div className="text">{msg.time}</div>
                            {msg.delivered ? (
                              <MessageOutline
                                fontSize={24}
                                color={msg.seen ? "#007fda" : "#ffffff"}
                              />
                            ) : (
                              <SingleTickUnBold
                                fontSize={16}
                                color={"#ffffff"}
                              />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              )}
            </div>
          );
        })}

      {/* 👇 Scroll target at the end of all messages */}
      <div ref={messageEndRef} />
    </div>
  );
}
